// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate} from 'react-router-dom'
import {PreviewCard} from '../../../../common/component/PreviewCard'
import { SettingOption } from '../../../../common/component/DropdownMenu'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { parseLocalDate } from '../../../../util/Locale'
import { CustomInputRenderer } from '../../../../common/component/CustomInputRenderer'
import { useTaskGetByIdMutation } from '../Task.api'
import { useAppSelector } from '../../../store/Store'
import { selectTask, selectTaskLoading } from '../Task.slice'
import { ContactEmbedered } from '../../../contact/components/ContactEmbedered'
import { IconLabelContent } from '../../../../common/component/IconLabelContent'

interface Props {
  ticketId: string
  taskId: string
  loading: boolean
  setRowSelected: (rowId?: string) => void
}

export const TaskPreview: FC<Props> = ({ticketId, taskId, setRowSelected}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/tickets/${ticketId}/tasks/${taskId}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/tickets/${ticketId}/tasks/${taskId}/edit`),
    },
  ]

  const data = useAppSelector(selectTask)
  const dataLoading = useAppSelector(selectTaskLoading)
  const [taskGetById] = useTaskGetByIdMutation()

  useEffect(() => {
    if (!data || !data.task || data.task.id !== taskId) {
      taskGetById({id: taskId})
    }
  }, [data, taskGetById, taskId])

  return (
    <PreviewCard
      loading={dataLoading}
      title={
        data?.task && data.task.title
          ? `${parseLocalDate(
              data.task.createdAt,
              intl.formatMessage({id: 'I18N.LOCALE'})
            )} - ${data.task.title}`
          : ''
      }
      subTitle={data?.task?.sequence}
      pathOnClickHandler={`/tickets/${ticketId}/tasks/${taskId}`}
      closeOnClickHandler={() => setRowSelected(undefined)}
      settingOptions={settingOptions}
      content={
        data &&
        data.task && (
          <div>
            <div className='row my-10'>
              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.GENERAL'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                    value={
                      data.task.updatedAt
                        ? parseLocalDate(
                            data.task.updatedAt,
                            intl.formatMessage({id: 'I18N.LOCALE'})
                          )
                        : ''
                    }
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ID'})}
                    value={`${data.task.sequence}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'TICKET.TITLE'})}
                    object={
                      <span className='fs-6 ms-3 text-dark'>
                        <Link className='' to={`/tickets/${ticketId}/tasks/${taskId}`}>
                          {data.task.title}
                        </Link>
                      </span>
                    }
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.STATUS'})}
                    value={intl.formatMessage({
                      id: `DROPDOWN.TASK.STATUS.${data.task?.statusCode}`,
                    })}
                  ></CustomInputRenderer>

                  {data.task?.startedAt && (
                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.STARTED_DATE_TIME'})}
                      object={
                        <>
                          <span className='fs-6 mx-3 text-dark'>
                            {data.task?.startedAt
                              ? `${parseLocalDate(
                                  data.task.startedAt,
                                  intl.formatMessage({id: 'I18N.LOCALE'})
                                )}`
                              : ''}
                          </span>
                          <IconLabelContent
                            title={`${data.task?.startingTime} ${intl.formatMessage({
                              id: 'UTIL.MINUTES',
                            })}`}
                            icon={'bi bi-info-lg'}
                            active={true}
                            activeColor={'text-primary'}
                          />
                        </>
                      }
                    ></CustomInputRenderer>
                  )}

                  {data.task?.assignedTo && (
                    <>
                      <ContactEmbedered
                        label={intl.formatMessage({id: 'UTIL.ASSIGNED_TO'})}
                        contact={data.task?.assignedTo}
                      />
                      <CustomInputRenderer
                        label={intl.formatMessage({id: 'UTIL.ASSIGNED_DATE_TIME'})}
                        object={
                          <>
                            <span className='fs-6 mx-3 text-dark'>
                              {data.task?.assignedAt
                                ? `${parseLocalDate(
                                    data.task.assignedAt,
                                    intl.formatMessage({id: 'I18N.LOCALE'})
                                  )}`
                                : ''}
                            </span>
                            <IconLabelContent
                              title={`${data.task?.assignationTime} ${intl.formatMessage({
                                id: 'UTIL.MINUTES',
                              })}`}
                              icon={'bi bi-info-lg'}
                              active={true}
                              activeColor={'text-primary'}
                            />
                          </>
                        }
                      ></CustomInputRenderer>
                    </>
                  )}
                </div>
              </div>

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.METRICS'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                    value={`${data.task.totalAttachments || 0}`}
                  ></CustomInputRenderer>
                </div>
              </div>
            </div>
          </div>
        )
      }
    ></PreviewCard>
  )
}
